import React, { useRef, useState } from 'react';
import { FiUpload, FiLink, FiLoader } from 'react-icons/fi';
import * as fabric from 'fabric';
import useEditorStore from '../../../stores/useEditorStore';
import { parseFile, getFileType } from '../../../services/editorService';
import { loadEditorDocument } from '../../../services/firestoreService';
import { convertSlide } from '../../../services/parserConverter';

const UploadSettingsPanel = () => {
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');

  const {
    canvas,
    pushHistory,
    setActiveTool,
    setCanvasSize,
    syncLayersFromCanvas,
    setSlidesData,
  } = useEditorStore();

  // 파일 업로드 핸들러 (문서 + 이미지 통합)
  const handleFileUpload = async (file) => {
    if (!file || !canvas) return;

    const fileType = getFileType(file.name);

    if (fileType) {
      // PPT, SVG, PSD 파일
      await handleDocumentUpload(file);
    } else if (file.type.startsWith('image/')) {
      // 이미지 파일
      await handleImageUpload(file);
    } else {
      alert('지원하지 않는 파일 형식입니다.\n(SVG, PPT, PPTX, PSD, PDF, PNG, JPG, GIF)');
    }
  };

  // 이미지 업로드 핸들러
  const handleImageUpload = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const img = await fabric.FabricImage.fromURL(event.target.result);

          const maxWidth = canvas.width * 0.8;
          const maxHeight = canvas.height * 0.8;
          const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);

          img.set({
            left: canvas.width / 2,
            top: canvas.height / 2,
            originX: 'center',
            originY: 'center',
            scaleX: scale,
            scaleY: scale,
            id: `image-${Date.now()}`,
            name: file.name,
          });

          canvas.add(img);
          canvas.setActiveObject(img);
          canvas.renderAll();
          pushHistory();
          syncLayersFromCanvas();
          setActiveTool('image');
        } catch (err) {
          console.error('Image load error:', err);
        }
        resolve();
      };
      reader.readAsDataURL(file);
    });
  };

  // 문서 파일 업로드 핸들러 (PPT, SVG, PSD)
  const handleDocumentUpload = async (file) => {
    setIsLoading(true);
    setLoadingMessage(`${file.name} 파싱 중...`);

    try {
      const result = await parseFile(file);
      if (!result.success) throw new Error(result.error || '파일 파싱 실패');

      setLoadingMessage('Firestore에서 데이터 로드 중...');
      const docResult = await loadEditorDocument(result.doc_id);
      if (!docResult.success) throw new Error(docResult.error || '문서 로드 실패');

      const { data } = docResult;

      canvas.clear();
      canvas.backgroundColor = '#ffffff';

      const [canvasWidth, canvasHeight] = data.canvas_size;
      setCanvasSize({ width: canvasWidth, height: canvasHeight });

      setLoadingMessage('캔버스에 객체 추가 중...');

      const slidesForStore = data.slides.map(slide => ({
        layers: slide.layers,
        canvasState: null,
      }));
      setSlidesData(slidesForStore, result.doc_id);

      if (data.slides && data.slides.length > 0) {
        const firstSlide = data.slides[0];
        const fabricObjects = await convertSlide(firstSlide.layers);
        for (const obj of fabricObjects) {
          canvas.add(obj);
        }
      }

      canvas.renderAll();
      syncLayersFromCanvas();
      pushHistory();

      setLoadingMessage('완료!');
      setTimeout(() => {
        setIsLoading(false);
        setLoadingMessage('');
      }, 500);

      setActiveTool('select');
    } catch (err) {
      console.error('Document upload error:', err);
      alert(`파일 업로드 오류: ${err.message}`);
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  // input onChange 핸들러
  const handleInputChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) await handleFileUpload(file);
    e.target.value = '';
  };

  // URL로 이미지 추가
  const handleImageFromURL = async () => {
    const url = prompt('이미지 URL을 입력하세요:');
    if (!url || !canvas) return;

    try {
      const img = await fabric.FabricImage.fromURL(url, { crossOrigin: 'anonymous' });

      const maxWidth = canvas.width * 0.8;
      const maxHeight = canvas.height * 0.8;
      const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);

      img.set({
        left: canvas.width / 2,
        top: canvas.height / 2,
        originX: 'center',
        originY: 'center',
        scaleX: scale,
        scaleY: scale,
        id: `image-${Date.now()}`,
        name: 'Image from URL',
      });

      canvas.add(img);
      canvas.setActiveObject(img);
      canvas.renderAll();
      pushHistory();
      syncLayersFromCanvas();
      setActiveTool('image');
    } catch (err) {
      alert('이미지를 불러올 수 없습니다. URL을 확인해주세요.');
      console.error('Image load error:', err);
    }
  };

  // 드래그 앤 드롭 핸들러
  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = '#3b82f6';
    e.currentTarget.style.backgroundColor = '#eff6ff';
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = '#e5e7eb';
    e.currentTarget.style.backgroundColor = '#fafafa';
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = '#e5e7eb';
    e.currentTarget.style.backgroundColor = '#fafafa';

    const file = e.dataTransfer.files?.[0];
    if (file) await handleFileUpload(file);
  };

  return (
    <div style={panelStyle}>
      <h3 style={titleStyle}>파일 업로드</h3>

      {/* 로딩 표시 */}
      {isLoading && (
        <div style={loadingStyle}>
          <FiLoader size={24} style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ margin: '8px 0 0', fontSize: '13px' }}>{loadingMessage}</p>
          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* 통합 드래그 앤 드롭 영역 */}
      <div style={sectionStyle}>
        <div
          onClick={() => !isLoading && fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            border: '2px dashed #e5e7eb',
            borderRadius: '12px',
            padding: '32px 16px',
            textAlign: 'center',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            transition: 'all 0.15s',
            backgroundColor: '#fafafa',
            opacity: isLoading ? 0.6 : 1,
          }}
          onMouseEnter={(e) => {
            if (!isLoading) {
              e.currentTarget.style.borderColor = '#3b82f6';
              e.currentTarget.style.backgroundColor = '#eff6ff';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e5e7eb';
            e.currentTarget.style.backgroundColor = '#fafafa';
          }}
        >
          <FiUpload size={36} color="#9ca3af" style={{ marginBottom: '12px' }} />
          <p style={{ margin: 0, fontSize: '14px', color: '#374151', fontWeight: '500' }}>
            파일 업로드
          </p>
          <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#6b7280' }}>
            클릭하거나 드래그 앤 드롭
          </p>
          <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#9ca3af' }}>
            PPT, SVG, PSD, PDF, PNG, JPG, GIF
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".ppt,.pptx,.svg,.psd,.pdf,image/*"
          onChange={handleInputChange}
          style={{ display: 'none' }}
          disabled={isLoading}
        />
      </div>

      {/* URL로 추가 */}
      <div style={sectionStyle}>
        <button
          onClick={handleImageFromURL}
          disabled={isLoading}
          style={{
            width: '100%', padding: '12px 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#ffffff',
            cursor: isLoading ? 'not-allowed' : 'pointer', fontSize: '14px', color: '#374151',
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          <FiLink size={18} />
          URL로 이미지 추가
        </button>
      </div>
    </div>
  );
};

const panelStyle = { padding: '16px', height: '100%', overflowY: 'auto' };

const titleStyle = {
  fontSize: '16px', fontWeight: '600', color: '#1f2937',
  marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb',
};

const sectionStyle = { marginBottom: '20px' };

const loadingStyle = {
  display: 'flex', flexDirection: 'column', alignItems: 'center',
  justifyContent: 'center', padding: '20px', marginBottom: '20px',
  backgroundColor: '#f3f4f6', borderRadius: '12px', color: '#374151',
};

export default UploadSettingsPanel;
